/**
 * 时间轴布局纯函数（§3.2 utils/timelineLayout.ts，可单测）
 * 预览与导出共用的唯一换算源（§4.7 变速换算，防两处口径漂移）。
 */
import type {
  AudioClip,
  CanvasRatio,
  CanvasResolution,
  Clip,
  EditDocument,
  TextClip,
  Track,
} from '@/types/smartEdit';

export type AnyClip = Clip | TextClip | AudioClip;

export function isVideoClip(c: AnyClip): c is Clip {
  return (c as Clip).sourceOut !== undefined && (c as TextClip).content === undefined && (c as Clip).speed !== undefined;
}

export function isTextClip(c: AnyClip): c is TextClip {
  return (c as TextClip).content !== undefined && (c as TextClip).end !== undefined;
}

export function isAudioClip(c: AnyClip): c is AudioClip {
  return (c as AudioClip).sourceOut !== undefined && (c as AudioClip).loop !== undefined;
}

/** 片段时间轴时长 = (sourceOut - sourceIn) / speed（§4.7） */
export function clipTimelineDuration(clip: Clip): number {
  const dur = (clip.sourceOut - clip.sourceIn) / (clip.speed || 1);
  return Math.max(0, dur);
}

export function textClipDuration(clip: TextClip): number {
  return Math.max(0, clip.end - clip.start);
}

export function audioClipDuration(clip: AudioClip): number {
  return Math.max(0, clip.sourceOut - clip.sourceIn);
}

/**
 * 主视频轨（V1）= 显示最底的视频轨（tracks 数组中视频组最后一个）。
 * 视频新轨插入视频组头部（V2/V3… 在上），因此回落/封面/字幕/预览等
 * 单一视频轨语义的锚点必须用本函数，而非 find(kind === 'video')（会命中顶部新轨）。
 */
export function primaryVideoTrack(doc: EditDocument): Track | undefined {
  const videos = doc.tracks.filter((t) => t.kind === 'video');
  return videos[videos.length - 1];
}

/** 单轨总时长 = Σ 片段时间轴时长（文档级推导值，不存储） */
export function videoTrackDuration(track: Track | undefined): number {
  if (!track) return 0;
  return track.clips.reduce((sum, c) => {
    if (isVideoClip(c)) return sum + clipTimelineDuration(c);
    if (isTextClip(c)) return sum + textClipDuration(c);
    if (isAudioClip(c)) return sum + audioClipDuration(c);
    return sum;
  }, 0);
}

/** 文档总时长 = max(视频轨, 各 text/audio 轨末端) */
export function documentDuration(doc: EditDocument): number {
  let max = 0;
  for (const track of doc.tracks) {
    let end = 0;
    for (const c of track.clips) {
      if (isVideoClip(c)) end = Math.max(end, (c.start ?? 0) + clipTimelineDuration(c));
      else if (isTextClip(c)) end = Math.max(end, c.end);
      // 音频对文档时长贡献：loop 曲循环补齐由视频轨主导（不虚增总时长）；独立音效按 start + 单次时长
      else if (isAudioClip(c)) end = Math.max(end, (c.start ?? 0) + (c.loop ? 0 : audioClipDuration(c)));
    }
    max = Math.max(max, end);
  }
  return max;
}

/**
 * 序列式布局：按 clips 数组顺序重排 start（首尾相接）。
 * V1 序列式（§3.1）：视频轨片段首尾相接，start 由顺序推导、显式冗余存储。
 */
export function layoutVideoClips(clips: Clip[]): Clip[] {
  let cursor = 0;
  return clips.map((c) => {
    const next = { ...c, start: cursor };
    cursor += clipTimelineDuration(c);
    return next;
  });
}

/** 调序：把 fromIndex 的片段移到 toIndex，随后整体重排 start */
export function reorderClip(clips: Clip[], fromIndex: number, toIndex: number): Clip[] {
  if (fromIndex === toIndex) return layoutVideoClips(clips);
  const next = [...clips];
  const [moved] = next.splice(fromIndex, 1);
  const target = Math.max(0, Math.min(next.length, toIndex > fromIndex ? toIndex - 1 : toIndex));
  next.splice(target, 0, moved);
  return layoutVideoClips(next);
}

/**
 * 按时间轴时间定位片段（线性扫描）。
 * 自由定位后片段数组可能乱序（start 不再由布局保证递增），线性扫描对任意顺序均正确。
 */
export function findClipAtTime(clips: Clip[], t: number): { clip: Clip; index: number } | null {
  for (let i = 0; i < clips.length; i++) {
    const c = clips[i]!;
    const dur = clipTimelineDuration(c);
    if (t >= c.start && t < c.start + dur) return { clip: c, index: i };
  }
  return null;
}

/** 时间轴绝对时间 T → 片段源时间 sourceIn + (T - clip.start) × speed（§4.7 模型） */
export function timelineToSourceTime(clip: Clip, t: number): number {
  return clip.sourceIn + (t - clip.start) * (clip.speed || 1);
}

/** 分割：播放头落在片段区间内时一刀两段（§4.4），返回新 clips 数组（未重排 start）。
 *  afterLinkId（达芬奇分割语义）：关联片段的「新半段」改用该 linkId——与同样被分割出的配对新半段
 *  重新配对，旧半段保留原 linkId，两组各自联动、互不牵连；不传 = after 段沿用原 linkId（兼容旧行为） */
export function splitClipAt(clips: Clip[], t: number, minDurationSec = 0.2, afterLinkId?: string): Clip[] | null {
  const found = findClipAtTime(clips, t);
  if (!found) return null;
  const { clip, index } = found;
  const local = timelineToSourceTime(clip, t);
  if (local - clip.sourceIn < minDurationSec || clip.sourceOut - local < minDurationSec * (clip.speed || 1)) {
    // 前后段均不得低于最小片段时长（源时长按速度换算）
    if (local - clip.sourceIn < minDurationSec || (clip.sourceOut - local) < 1e-6) return null;
  }
  const before: Clip = { ...clip, id: clip.id, sourceOut: local };
  // 新片段起点 = 分割点（序列式布局：不重排其余片段，但新片段自身 start 必须衔接）
  const after: Clip = {
    ...clip,
    id: `${clip.id}-b${Date.now().toString(36)}`,
    sourceIn: local,
    start: t,
    ...(clip.linkId != null && afterLinkId ? { linkId: afterLinkId } : {}),
  };
  const next = [...clips];
  next.splice(index, 1, before, after);
  return next;
}

/** 分割音频片段：播放头 t 落于 [start, start+dur) 内一刀两段，返回新数组；未命中/过短返回 null。
 *  afterLinkId：同 splitClipAt——关联音频的「新半段」改用该 linkId 与配对新半段重新配对 */
export function splitAudioClipsAt(
  clips: AudioClip[],
  t: number,
  minDurationSec = 0.2,
  afterLinkId?: string,
): AudioClip[] | null {
  const idx = clips.findIndex((c) => t >= c.start && t < c.start + audioClipDuration(c));
  if (idx < 0) return null;
  const clip = clips[idx]!;
  const offset = t - clip.start;
  if (offset < minDurationSec || audioClipDuration(clip) - offset < minDurationSec) return null;
  const before: AudioClip = { ...clip, sourceOut: clip.sourceIn + offset };
  const after: AudioClip = {
    ...clip,
    id: `${clip.id}-b${Date.now().toString(36)}`,
    sourceIn: clip.sourceIn + offset,
    start: t,
    ...(clip.linkId != null && afterLinkId ? { linkId: afterLinkId } : {}),
  };
  const next = [...clips];
  next.splice(idx, 1, before, after);
  return next;
}

/**
 * 收集吸附候选点（秒，升序去重）：0 点 / 播放头 / 所有片段的入点与出点。
 * excludeClipId：拖拽中的片段自身不参与（否则自身边缘会吸住自己）。
 */
export function collectSnapPoints(
  doc: EditDocument,
  currentTime: number,
  excludeClipId?: string,
  excludeLinkId?: string,
): number[] {
  const pts = new Set<number>([0, currentTime]);
  for (const track of doc.tracks) {
    for (const c of track.clips) {
      if (excludeClipId && c.id === excludeClipId) continue;
      // 排除同 linkId 配对片段：其边缘随联动同步移动且常与被拖/被修剪片段重合，
      // 旧位置吸附恒等「吸回原位/吸到零帧」（修剪扩展 head 时尤甚），不作为候选点
      if (excludeLinkId && (isVideoClip(c) || isAudioClip(c)) && c.linkId === excludeLinkId) continue;
      if (isVideoClip(c)) {
        pts.add(c.start);
        pts.add(c.start + clipTimelineDuration(c));
      } else if (isTextClip(c)) {
        pts.add(c.start);
        pts.add(c.end);
      } else if (isAudioClip(c)) {
        pts.add(c.start);
        pts.add(c.start + audioClipDuration(c));
      }
    }
  }
  return [...pts].sort((a, b) => a - b);
}

/** 吸附：time 距最近候选点 ≤ thresholdSec 时返回候选点，否则原值（snapped 标记是否命中） */
export function snapToPoints(time: number, points: number[], thresholdSec: number): { time: number; snapped: boolean } {
  let best: number | null = null;
  let bestDist = Infinity;
  for (const p of points) {
    const d = Math.abs(p - time);
    if (d < bestDist) {
      bestDist = d;
      best = p;
    }
  }
  if (best !== null && bestDist <= thresholdSec) return { time: best, snapped: true };
  return { time, snapped: false };
}

/** 修剪单边缘可达区间（timeline 秒）：min/max 为该边缘（head=片段 start / tail=片段 end）可到达的时间值 */
export interface TrimEdgeRange {
  min: number;
  max: number;
}

/**
 * 达芬奇式边缘修剪可达区间（普通修剪：不与相邻片段重叠）。
 * - head（左边缘）下限：前邻 end / 源入点耗尽（start_new ≥ start − sourceIn/speed，保证 sourceIn ≥ 0）/ 0
 *   三者取大；上限：保最短片段时长（end − MIN）。
 * - tail（右边缘）下限：保最短片段时长；上限：min(后邻 start, 源长度收口 start + (sourceDuration − sourceIn)/speed)。
 *   修剪 = 从源素材剪出需要的片段，扩展不能拖出素材真实长度；sourceDuration 缺省 = 旧文档不限
 *   （装载/打开面板时自动回填）；loop 循环铺满的音频调用方不传该字段（向右拉长合法）。
 * - 文字片段无源素材：sourceIn 传 Infinity（源入点不设限，仅相邻与最短时长约束生效）。
 */
export function trimEdgeBounds(params: {
  clipStart: number;
  clipTimelineDur: number;
  sourceIn: number;
  speed: number;
  prevEnd: number;
  nextStart: number | null;
  minClipSec: number;
  /** 源素材总时长（秒）：右缘扩展硬上限；缺省 = 不限（旧文档兼容） */
  sourceDuration?: number;
}): { head: TrimEdgeRange; tail: TrimEdgeRange } {
  const { clipStart, clipTimelineDur, sourceIn, speed, prevEnd, nextStart, minClipSec, sourceDuration } = params;
  const end = clipStart + clipTimelineDur;
  // 源长度收口：剩余源长度换算到时间线（Δ源 = Δtimeline × speed）；退化（源余量不足最短时长）时保底最短时长
  const sourceTailMax =
    sourceDuration != null
      ? clipStart + Math.max((sourceDuration - sourceIn) / (speed || 1), minClipSec)
      : Infinity;
  return {
    head: {
      min: Math.max(prevEnd, clipStart - sourceIn / (speed || 1), 0),
      max: end - minClipSec,
    },
    tail: {
      min: clipStart + minClipSec,
      max: Math.min(nextStart ?? Infinity, sourceTailMax),
    },
  };
}

/** 关联素材修剪联动补丁（达芬奇式）：被修剪片段的同 linkId 配对片段同步修剪——
 *  head：start 与 sourceIn 同步移动 Δtimeline（Δ源 = Δtimeline × 自身速度，各自 clamp ≥0，源耗尽即停）；
 *  tail：sourceOut 延长 Δtimeline × 自身速度（音频恒 1，视频按自身变速换算），
 *  且不超配对片段自身源长度 sourceDuration（配对片段必为非循环，缺省 = 旧文档不限）。
 *  非关联片段（无 linkId 的视频/音频、文字片段）返回 null（不联动）。 */
export function linkedTrimPatch(
  c: Clip | TextClip | AudioClip,
  link: { edge: 'head' | 'tail'; deltaSec: number },
): Partial<Pick<Clip, 'start' | 'sourceIn' | 'sourceOut'>> | null {
  if (!isVideoClip(c) && !isAudioClip(c)) return null;
  const spd = isVideoClip(c) ? c.speed || 1 : 1;
  if (link.edge === 'head') {
    return {
      start: Math.max(0, c.start + link.deltaSec),
      sourceIn: Math.max(0, c.sourceIn + link.deltaSec * spd),
    };
  }
  return { sourceOut: Math.min(c.sourceOut + link.deltaSec * spd, c.sourceDuration ?? Infinity) };
}

/** 覆盖修剪联动补丁记录：被覆盖片段的 linkId → 等量修剪（供配对片段在其他轨道应用 linkedTrimPatch） */
export interface OverwriteLinkedPatch {
  linkId: string;
  edge: 'head' | 'tail';
  deltaSec: number;
}

/** 中切右段音频重建信息：被切片段带 linkId 时，右段视频改挂新 linkId，
 *  调用方据此在音频轨克隆右段配对音频（原音频经 linkedPatches tail 修剪后成为左段音频） */
export interface OverwriteSplitAudio {
  /** 被切片段原 linkId */
  originalLinkId: string;
  /** 右段视频与新建右段音频共享的新 linkId */
  newLinkId: string;
  /** 右段时间线入点（= 右段视频 start） */
  start: number;
  /** 右段音频源入点偏移：sourceIn + headDeltaSec */
  headDeltaSec: number;
  /** 右段音频源出点还原：sourceOut - tailDeltaSec（tailDeltaSec 为负 → 还原原出点） */
  tailDeltaSec: number;
}

export interface OverwriteTrimResult {
  clips: AnyClip[];
  linkedPatches: OverwriteLinkedPatch[];
  splitAudios: OverwriteSplitAudio[];
}

/**
 * 单轨覆盖修剪（达芬奇「覆盖放置」）：moved 片段占据 [movedStart, movedStart+时长)，
 * 与同轨其他片段逐一求交（自由定位下片段无重叠约束，交叠即按覆盖处理）：
 * - 仅左残余足够 → 修剪对方尾部（sourceOut/end 收缩至覆盖起点）；
 * - 仅右残余足够 → 修剪对方头部（start/sourceIn 前移至覆盖终点）；
 * - 两侧残余均足够 → 切成两段（左段保留原 id/linkId 并记录 tail 联动；右段新 id，带 linkId 时改挂
 *   新 linkId 并记录 splitAudios，由调用方在音频轨克隆右段配对音频——两段视频各自保有配对音频）；
 * - 残余不足 minDurationSec → 删除对方（同 removeClip 语义：不级联配对片段）。
 * 带配对（linkId）的被修剪片段按自身速度换算记录等量联动补丁，由调用方对其他轨道
 * 同 linkId 片段应用 linkedTrimPatch（本轨片段已直接修剪，调用方须跳过本轨）。
 * 未命中 moved 或无任何覆盖时返回 null（调用方可跳过轨道重建）。
 */
export function overwriteTrimTrackClips(
  clips: AnyClip[],
  movedId: string,
  movedStart: number,
  genId: () => string,
  minDurationSec = 0.2,
): OverwriteTrimResult | null {
  const moved = clips.find((c) => c.id === movedId);
  if (!moved) return null;
  const movedDur = isVideoClip(moved)
    ? clipTimelineDuration(moved)
    : isTextClip(moved)
      ? textClipDuration(moved)
      : audioClipDuration(moved);
  const movedEnd = movedStart + movedDur;
  let changed = false;
  const linkedPatches: OverwriteLinkedPatch[] = [];
  const splitAudios: OverwriteSplitAudio[] = [];
  const out: AnyClip[] = [];
  for (const c of clips) {
    if (c.id === movedId) {
      out.push(c);
      continue;
    }
    const cStart = c.start;
    const cDur = isVideoClip(c)
      ? clipTimelineDuration(c)
      : isTextClip(c)
        ? textClipDuration(c)
        : audioClipDuration(c);
    const cEnd = cStart + cDur;
    const oStart = Math.max(cStart, movedStart);
    const oEnd = Math.min(cEnd, movedEnd);
    if (oEnd <= oStart) {
      out.push(c); // 无重叠
      continue;
    }
    const leftDur = oStart - cStart;
    const rightDur = cEnd - oEnd;
    const spd = isVideoClip(c) ? c.speed || 1 : 1;
    const linkId = isVideoClip(c) || isAudioClip(c) ? c.linkId : undefined;
    const record = (edge: 'head' | 'tail', deltaSec: number) => {
      if (linkId) linkedPatches.push({ linkId, edge, deltaSec });
    };
    const trimTail = () =>
      isVideoClip(c)
        ? { ...c, sourceOut: c.sourceOut + (oStart - cEnd) * spd }
        : isTextClip(c)
          ? { ...c, end: oStart }
          : { ...c, sourceOut: c.sourceOut + (oStart - cEnd) };
    const trimHead = (opts?: { newId?: string; newLinkId?: string }) =>
      isVideoClip(c)
        ? {
            ...c,
            id: opts?.newId ?? c.id,
            start: oEnd,
            sourceIn: c.sourceIn + (oEnd - cStart) * spd,
            ...(opts?.newLinkId ? { linkId: opts.newLinkId } : {}),
          }
        : isTextClip(c)
          ? { ...c, id: opts?.newId ?? c.id, start: oEnd }
          : {
              ...c,
              id: opts?.newId ?? c.id,
              start: oEnd,
              sourceIn: c.sourceIn + (oEnd - cStart),
              ...(opts?.newLinkId ? { linkId: opts.newLinkId } : {}),
            };
    if (leftDur >= minDurationSec && rightDur >= minDurationSec) {
      // 中间覆盖：切成两段——左段保留原 id/linkId（联动 tail）；右段新 id，带 linkId 时改挂新 linkId
      //（调用方据此在音频轨克隆右段配对音频，两段视频各自保有配对音频）
      const newLinkId = linkId ? genId() : undefined;
      out.push(trimTail(), trimHead({ newId: genId(), newLinkId }));
      const tailDelta = oStart - cEnd;
      record('tail', tailDelta);
      if (linkId && newLinkId) {
        splitAudios.push({
          originalLinkId: linkId,
          newLinkId,
          start: oEnd,
          headDeltaSec: oEnd - cStart,
          tailDeltaSec: tailDelta,
        });
      }
    } else if (leftDur >= minDurationSec) {
      out.push(trimTail());
      record('tail', oStart - cEnd);
    } else if (rightDur >= minDurationSec) {
      out.push(trimHead());
      record('head', oEnd - cStart);
    }
    // else：完全覆盖 / 残余均不足 → 删除（不级联配对片段）
    changed = true;
  }
  if (!changed) return null;
  return { clips: out, linkedPatches, splitAudios };
}

/** 配对自愈补丁：需重新对齐视频锚点的音频片段 */
export interface LinkedPairPatch {
  clipId: string;
  start: number;
  sourceIn: number;
  sourceOut: number;
}

/**
 * 配对自愈（打开编辑器时静默执行）：扫描音频轨，找「同 linkId 且同 src」的音视频配对组，
 * 音频的 start/sourceIn/sourceOut 与视频锚点不一致（历史残留：关联断开期间被独立移动/修剪）
 * 时，生成重新对齐视频锚点的补丁——恢复 pairClip 创建时的配对规范态。
 * 1x 速度下关联修剪全程保持三字段精确相等（head 双方同移 Δ、tail 双方都不动 start），
 * 故「同组同源却不等」必为残留错位，自愈不会误伤修剪结果；已对齐（1ms 容差）、
 * 非同源配对（用户手动挂载的外部音频，无「同一画面段」语义）不动。
 */
export function realignLinkedPairPatches(tracks: Track[]): LinkedPairPatch[] {
  // linkId → 视频锚点（同组多个视频时取首个）
  const anchors = new Map<string, Clip>();
  for (const t of tracks) {
    if (t.kind !== 'video') continue;
    for (const c of t.clips) {
      if (isVideoClip(c) && c.linkId && !anchors.has(c.linkId)) anchors.set(c.linkId, c);
    }
  }
  const eps = 1e-3; // 1ms 容差：浮点修剪数学的亚毫秒误差不视为错位
  const patches: LinkedPairPatch[] = [];
  for (const t of tracks) {
    if (t.kind !== 'audio') continue;
    for (const c of t.clips) {
      if (!isAudioClip(c) || !c.linkId) continue;
      const v = anchors.get(c.linkId);
      if (!v || v.src !== c.src) continue;
      if (
        Math.abs(c.start - v.start) < eps &&
        Math.abs(c.sourceIn - v.sourceIn) < eps &&
        Math.abs(c.sourceOut - v.sourceOut) < eps
      ) {
        continue;
      }
      patches.push({ clipId: c.id, start: v.start, sourceIn: v.sourceIn, sourceOut: v.sourceOut });
    }
  }
  return patches;
}

/** 时间格式化 00:00（工具栏中组 / 标尺） */
export function formatTimecode(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  const m = Math.floor(s / 60);
  return `${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

/** dB → 线性增益（预览 video.volume / 导出 volume 滤镜共用） */
export function dbToLinear(db: number): number {
  return Math.max(0, Math.min(1, Math.pow(10, db / 20)));
}

/** dB → FFmpeg volume 滤镜参数（-∞ 静音映射 0） */
export function dbToVolumeFilter(db: number): string {
  if (db <= -60) return 'volume=0';
  return `volume=${db.toFixed(2)}dB`;
}

/**
 * 文档合法性校验（导出前仅校验文档合法性——负数、空视频轨、区间非法）。
 * 自由定位（§4.4）下同轨间隙 / 重叠均为合法编辑结果，不再校验重叠。
 * 返回错误清单；空数组 = 合法。
 */
export function validateDocument(doc: EditDocument): string[] {
  const errors: string[] = [];
  // 多视频轨（V1/V2…）：任一视频轨存在即可，全部为空才报错
  const videoTracks = doc.tracks.filter((t) => t.kind === 'video');
  if (videoTracks.length === 0) errors.push('缺少视频轨');
  if (videoTracks.length > 0 && videoTracks.every((t) => t.clips.filter(isVideoClip).length === 0)) {
    errors.push('视频轨没有任何片段');
  }
  for (const track of doc.tracks) {
    for (const c of track.clips) {
      if (isVideoClip(c)) {
        if (c.sourceOut <= c.sourceIn) errors.push(`片段源区间非法（${c.id}）`);
        if (c.sourceIn < 0) errors.push(`片段源入点为负（${c.id}）`);
      } else if (isTextClip(c)) {
        if (c.end <= c.start) errors.push(`文字片段区间非法（${c.id}）`);
      }
    }
  }
  return errors;
}

/** 标尺刻度步长：按 pxPerSec 选择可读刻度间隔（秒） */
export function rulerStep(pxPerSec: number): number {
  const candidates = [0.5, 1, 2, 5, 10, 30, 60, 120, 300];
  for (const c of candidates) {
    if (c * pxPerSec >= 60) return c;
  }
  return candidates[candidates.length - 1];
}

/** 时间码简写（标尺）：0s / 00:01 / 01:30 */
export function formatRulerLabel(sec: number): string {
  if (sec < 60 && Number.isInteger(sec)) return `${sec}s`;
  return formatTimecode(sec);
}

/** 规格快捷推导（输入栏规格选择器） */
export function deriveSpec(
  spec?: { ratio: CanvasRatio; resolution: CanvasResolution }
): { ratio: CanvasRatio; resolution: CanvasResolution } {
  return spec ?? { ratio: '16:9', resolution: '720P' };
}
